import { DatePipe, TitleCasePipe } from '@angular/common';
import { createReducer, on } from '@ngrx/store';
import { AccountInfoActions } from '../../account-info-header/actions';
import { InitReleaseActions } from '../../initiate-and-release/actions';
import { DraftListAttributes } from '../../initiate-and-release/Models/Drafts/DraftResponse';
import { SharedActions } from '../../shared/actions';
import { FormatPercentPipe } from '../../shared/pipes/FormatPercentPipe';
import { amountFormatter } from '../../shared/utilities/AmountFormatter';
import { ApiEndpointType, CheckAbbreviatedRecipientTypes, checkRecipientTypeDict, Constants, formType, transactionTypes, yesNo } from '../../shared/utilities/Constants';
import { ViewPageActions } from '../../transaction-view/actions';
import {
  AchActions,
  CheckActions,
  ConfirmationPageActions,
  InternalTransferActions,
  InternalTransfersActions,
  PtrsActions,
  ReviewPageActions, StandingAuthorizationsActions, StandingAuthorizationsTableActions, TransactionDetailsActions,
  TransactionFormActions, TransactionsTableActions, WireActions
} from '../actions';
import { AchForm, AchTransactionDetails, CheckTransactionDetails, ChecksExtraPrompts, ItExtraPrompts, ItTransactionDetails, TransactionForm, TransactionFormServiceError, WireExtraPrompts, WireTransactionDetails, isAchForm, prefilledFlowNames, prefilledFlowStates, AchExtraPrompts } from '../models/form/TransactionForm';
import { PayeeTypes } from '../models/wire/WireInputResponseBase';
import { DistributionReason } from '../models/internal-transfer/DistributionReason';
import { CodeAndDescription } from '../models/shared/CodeAndDescription';

export const transactionFormKey = 'transactionForm';

export type State = TransactionForm;

export const initialTransactionFormServiceError: TransactionFormServiceError = {
  status: 0,
  url: '',
  name: '',
  message: '',
  correlationId: '',
  hasError: false,
}

export const initialTransactionFormState: State = {
  loading: false,
  error: '',
  hasError: false,
  transactionType: '',
  extraPrompts: {
    useStandingAuth: '',
  },
  transactionDetails: {},
  valid: false,
  prefilledFlow: {
    state: '',
    name: ''
  },
  serviceError: initialTransactionFormServiceError,
  showCashieringIRAAgreement: false,
  createTransactionInProgress: false,
  acknowledgeCallV3FailedForToggleOn: false,
  acknowledgeCallV3Failed: false
};

export const transactionFormReducer = createReducer(
  initialTransactionFormState,
  on(TransactionFormActions.updateFormValue,
    (state, { type, ...update }) => {
      const updated: TransactionForm = {
        ...state,
        transactionType: update.transactionType || '',
      }

      if (update.extraPrompts) {
        updated.extraPrompts = update.extraPrompts;
      }
      if (update.transactionDetails && Object.values(update.transactionDetails).some(detail => detail !== null && detail !== undefined)) { //only update if at least one value is not null/undefined.
        updated.transactionDetails = update.transactionDetails;
      }
      return updated;
    }),
  on(TransactionFormActions.updateFormStatus,
    (state, { type, ...valid }) => {
      return {
        ...state,
        ...valid,
        transactionDetails: {
          ...state.transactionDetails,
        },
      };
    }),
  on(ReviewPageActions.editClicked, (state) => {
    return {
      ...state,
      loading: false,
      prefilledFlow: {
        state: prefilledFlowStates.progress,
        name: state.prefilledFlow.name !== prefilledFlowNames.editTable ? prefilledFlowNames.editReview : state.prefilledFlow.name
      },
      verifyCallSuccesful: false,
      showCashieringIRAAgreement: false,
    };
  }),
  on(TransactionFormActions.editLoadComplete, (state) => {
    return {
      ...state,
      prefilledFlow: {
        ...state.prefilledFlow,
        state: prefilledFlowStates.complete,
      }
    };
  }),
  on(TransactionFormActions.cancelClicked, (state) => {
    return {
      ...state,
      prefilledFlow: {
        ...initialTransactionFormState.prefilledFlow
      },
      verifyCallSuccesful: false,
    };
  }),
  on(AchActions.callAchInput, (state, response) => {
    return {
      ...state,
      loading: true,
      transactionDetails: {
        ...state.transactionDetails,
        selectedAuthorization: response.authorizationUniqueId,
      },
    };
  }),
  on(
    AchActions.callAchInput,
    StandingAuthorizationsActions.loadSloas,
    InternalTransferActions.standingAuthSelected,
    CheckActions.standingAuthSelected,
    WireActions.callWireInputV3,
    WireActions.callWireInputV3TemplateFlow,
    CheckActions.callOneTimeInput,
    CheckActions.callRecurringInput,
    WireActions.callWireTemplatesGetList,
    (state) => {
      return {
        ...state,
        hasError: false,
        error: '',
        loading: true,
      };
    }
  ),
  on(
    TransactionDetailsActions.loadWithdrawalDateSuccess,
    InternalTransferActions.verifyCallSuccessful,
    InternalTransfersActions.verifyListCallSuccessful,
    AchActions.acknowledgeCallSuccessful,
    InternalTransferActions.acknowledgeCallSuccessful,
    CheckActions.acknowledgeCallSuccessful,
    WireActions.verifyV3CallSuccessful,
    WireActions.callWireSloaVerifyV3FromCreateTransactionSuccessful,
    WireActions.wireInputCallV3TemplateFlowSuccessful,
    CheckActions.verifyCallSuccessful,
    StandingAuthorizationsActions.loadSloasSuccess,
    WireActions.callWireTemplatesGetListSuccessful,
    TransactionFormActions.loadChecksReviewPageWithoutVerifyCall,
    TransactionFormActions.loadInternalTransferWithoutVerifyCall,
    (state) => {
      return {
        ...state,
        hasError: false,
        loading: false,
        serviceError: initialTransactionFormServiceError,
      };
    }
  ),
  on(WireActions.wireInputCallV3TemplateFlowSuccessful, (state, {response}) => {
    return {
      ...state,
      loading: false,
      serviceError: initialTransactionFormServiceError,
      showCashieringIRAAgreement:
        response.accountInfo?.isIraAccount &&
        !response.iraFields?.isCashieringIRAAgreementOpened,
      transactionDetails: {
        ...state.transactionDetails,
        trustIncomePercent:
          response.accountInfo.trustInfo?.trustIncomePercent.toString() +
          '%',
        trustPrincipalPercent:
          response.accountInfo.trustInfo?.trustPrincipalPercent.toString() +
          '%',
      },
    };
  }),
  on(WireActions.wireInputCallSuccessfulV3,
    (state, {response}) => {
      const isThreeStep = response.sloaInfo?.isoFinancialInstitutions?.length > 2;
      const isFourStep = response.sloaInfo?.isoFinancialInstitutions?.length > 3;
      const firstIntermediaryPosition = isFourStep ? 3 : (isThreeStep ? 2 : 1);
      // TODO: SAC-369214 remove the dynamic positioning logic and set by IsoUIFields
      // const isThreeStep = !!response?.sloaInfo?.isoUIFields?.secondIntermediary;
      // const isFourStep = !!response?.sloaInfo?.isoUIFields?.thirdIntermediary;
      // const isISOCompliant = response?.sloaInfo?.isISOCompliant;
      const recipient = response?.sloaInfo?.isoUIFields?.recipient;
      // const firstIntermediary = response?.sloaInfo?.isoUIFields?.firstIntermediary;
      const secondIntermediary = response?.sloaInfo?.isoUIFields?.secondIntermediary;
      const thirdIntermediary = response?.sloaInfo?.isoUIFields?.thirdIntermediary;
      // const recipientBank = response?.sloaInfo?.isoUIFields?.recipientBank;
      return {
        ...state,
        loading: false,
        serviceError: initialTransactionFormServiceError,
        showCashieringIRAAgreement:
          response.accountInfo?.isIraAccount &&
          !response.iraFields?.isCashieringIRAAgreementOpened,
        transactionDetails: {
          ...state.transactionDetails,
          trustIncomePercent:
            response.accountInfo?.trustInfo?.trustIncomePercent.toString() + '%',
          trustPrincipalPercent:
            response.accountInfo?.trustInfo?.trustPrincipalPercent.toString() +
            '%',
          amount: '',

          intermediarySelection: response.sloaInfo?.isoFinancialInstitutions.length > 1
            ? 'yes'
            : 'no',

          // Receiving Bank/Intermediary bank ABA DETAILS SECTION
          aba: response.sloaInfo?.isISOCompliant ?
            response.sloaInfo?.isoFinancialInstitutions[0]?.identifierList[0]?.identifierValue : response.sloaInfo?.wiresBankDetails?.receivingBankAba ?? '',
            receivingBankClearingSysId: response?.sloaInfo?.isoFinancialInstitutions[0]?.clearingSystemBankId?.identifierValue,
          bankID: response.sloaInfo?.isFedWire
            ? ''
            : response.sloaInfo?.wiresBankDetails?.receivingBankAba,
          receivingBankName:
            response.sloaInfo?.isoFinancialInstitutions[0]?.name,
          receivingBankIdentifierList: response.sloaInfo?.isoFinancialInstitutions[0]?.identifierList,
          receivingBankConcatenatedAddress:
            response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.concatenatedAddress?.trim(),
          receivingBankCountry: response.sloaInfo?.isISOCompliant ?
            response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.country?.trim() : response.sloaInfo?.wiresBankDetails?.receivingBankCountry?.trim(),
          bankPhoneNumber: response.sloaInfo?.isISOCompliant ?
            response.sloaInfo?.receivingBank?.mailingDetails?.phoneNumber : response.sloaInfo?.wiresBankDetails?.receivingBankPhone,
          receivingBankStreetNumber: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.buildingNumber?.trim(),
          receivingBankStreetName: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.streetName?.trim(),
          receivingBankApartmentSuiteEtc: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.room?.trim(),
          receivingBankCity: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.townName?.trim(),
          receivingBankState: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.countrySubDivision?.trim(),
          receivingBankZip: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.postCode?.trim(),
          receivingBankDepartment: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.department?.trim(),
          receivingBankBuildingName: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.buildingName?.trim(),
          receivingBankFloor: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.floor?.trim(),
          receivingBankTownLocationName: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.townLocationName?.trim(),
          receivingBankDistrict: response.sloaInfo?.isoFinancialInstitutions[0]?.postalAddress?.districtName?.trim(),
          // TODO: Will be removed as part of SAC-357406
          receivingBankName1: response.sloaInfo?.wiresBankDetails?.receivingBankName[0],
          receivingBankName2: response.sloaInfo?.wiresBankDetails?.receivingBankName[1],
          receivingBankAddress1: response.sloaInfo?.wiresBankDetails?.receivingBankAddress[0],
          receivingBankAddress2: response.sloaInfo?.wiresBankDetails?.receivingBankAddress[1],
          //Recipient Bank details section under Intermediary => addseperate fields
          intermediaryIdentifierList:
            response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList,
          intermediaryAccountNumber: response.sloaInfo?.isISOCompliant ?
            response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList.filter(x => x.identifierType == "Account")[0]?.identifierValue : response.sloaInfo?.wiresBankDetails?.intermediaryAccountNumber,
          intermediaryIBAN:
            response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList.filter(x => x.identifierType == "IBAN")[0]?.identifierValue,
          intermediaryBIC:
            response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.identifierList.filter(x => x.identifierType == "BIC")[0]?.identifierValue,
          intermediaryClearingSysId:
           response?.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.clearingSystemBankId?.identifierValue,
          intermediaryName: response.sloaInfo?.isISOCompliant ?
            (response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.name) : (response.sloaInfo?.isoFinancialInstitutions?.length > 1 ? response.sloaInfo?.wiresBankDetails?.intermediaryName[0] : ''),
          intermediaryConcatenatedAddress:
            response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.concatenatedAddress?.trim(),
          intermediaryCountry: response.sloaInfo?.isISOCompliant ?
            (response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.country?.trim()) : (response.sloaInfo?.isoFinancialInstitutions?.length > 1 ? response.sloaInfo?.wiresBankDetails?.intermediaryCountry : ''),
          intermediaryNameCont: response.sloaInfo?.isoFinancialInstitutions?.length > 1 ? response.sloaInfo?.wiresBankDetails?.intermediaryName[1] : '',
          intermediaryAddress: response.sloaInfo?.isoFinancialInstitutions?.length > 1 ? response.sloaInfo?.wiresBankDetails?.intermediaryAddress[0] : '',
          intermediaryAddressCont: response.sloaInfo?.isoFinancialInstitutions?.length > 1 ? response.sloaInfo?.wiresBankDetails?.intermediaryAddress[1] : '',
          intermediaryStreetNumber: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.buildingNumber?.trim(),
          intermediaryStreetName: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.streetName?.trim(),
          intermediaryApartmentSuiteEtc: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.room?.trim(),
          intermediaryCity: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.townName?.trim(),
          intermediaryState: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.countrySubDivision?.trim(),
          intermediaryZip: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.postCode?.trim(),
          intermediaryDepartment: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.department?.trim(),
          intermediaryBuildingName: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.buildingName?.trim(),
          intermediaryFloor: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.floor?.trim(),
          intermediaryTownLocationName: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.townLocationName?.trim(),
          intermediaryDistrict: response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.postalAddress?.districtName?.trim(),
          intermediaryPhone: response.sloaInfo?.isISOCompliant ?
            (response.sloaInfo?.isoFinancialInstitutions[firstIntermediaryPosition]?.phone) : (response.sloaInfo?.isoFinancialInstitutions?.length > 1 ? response.sloaInfo?.wiresBankDetails?.intermediaryPhone : ''),

          // second Intermediary Section
          creditToSelection: response.sloaInfo?.isoFinancialInstitutions?.length > 2
            ? 'yes'
            : 'no',
          isThreeStep: isThreeStep,
          secondIntermediaryIdentifierList: secondIntermediary?.identifierList,
          secondIntermediaryName: secondIntermediary?.name?.trim(),
          secondIntermediaryConcatenatedAddress:
            secondIntermediary?.postalAddress?.concatenatedAddress?.trim(),
          secondIntermediaryCountry: secondIntermediary?.postalAddress?.country?.trim(),
          secondIntermediaryStreetNumber: secondIntermediary?.postalAddress?.buildingNumber?.trim(),
          secondIntermediaryStreetName: secondIntermediary?.postalAddress?.streetName?.trim(),
          secondIntermediaryApartmentSuiteEtc: secondIntermediary?.postalAddress?.room?.trim(),
          secondIntermediaryCity: secondIntermediary?.postalAddress?.townName?.trim(),
          secondIntermediaryState: secondIntermediary?.postalAddress?.countrySubDivision?.trim(),
          secondIntermediaryZip: secondIntermediary?.postalAddress?.postCode?.trim(),
          secondIntermediaryDepartment: secondIntermediary?.postalAddress?.department?.trim(),
          secondIntermediaryBuildingName: secondIntermediary?.postalAddress?.buildingName?.trim(),
          secondIntermediaryFloor: secondIntermediary?.postalAddress?.floor?.trim(),
          secondIntermediaryTownLocationName: secondIntermediary?.postalAddress?.townLocationName?.trim(),
          secondIntermediaryDistrict: secondIntermediary?.postalAddress?.districtName?.trim(),
          secondIntermediaryAccountNumber: secondIntermediary?.accountNumber?.identifierValue,
          secondIntermediaryIBAN: secondIntermediary?.iban?.identifierValue,
          secondIntermediaryBIC: secondIntermediary?.bic?.identifierValue,
          secondIntermediaryClearingSysId: secondIntermediary?.clearingSystemBankId?.identifierValue,

          // Third Intermediary Section
          isFourStep: isFourStep,
          thirdIntermediaryIdentifierList: thirdIntermediary?.identifierList,
          thirdIntermediaryName:
            thirdIntermediary?.name?.trim(),
          thirdIntermediaryConcatenatedAddress:
            thirdIntermediary?.postalAddress?.concatenatedAddress?.trim(),
          thirdIntermediaryCountry:
            thirdIntermediary?.postalAddress?.country?.trim(),
          thirdIntermediaryStreetNumber: thirdIntermediary?.postalAddress?.buildingNumber?.trim(),
          thirdIntermediaryStreetName: thirdIntermediary?.postalAddress?.streetName?.trim(),
          thirdIntermediaryApartmentSuiteEtc: thirdIntermediary?.postalAddress?.room?.trim(),
          thirdIntermediaryCity: thirdIntermediary?.postalAddress?.townName?.trim(),
          thirdIntermediaryState: thirdIntermediary?.postalAddress?.countrySubDivision?.trim(),
          thirdIntermediaryZip: thirdIntermediary?.postalAddress?.postCode?.trim(),
          thirdIntermediaryDepartment: thirdIntermediary?.postalAddress?.department?.trim(),
          thirdIntermediaryBuildingName: thirdIntermediary?.postalAddress?.buildingName?.trim(),
          thirdIntermediaryFloor: thirdIntermediary?.postalAddress?.floor?.trim(),
          thirdIntermediaryTownLocationName: thirdIntermediary?.postalAddress?.townLocationName?.trim(),
          thirdIntermediaryDistrict: thirdIntermediary?.postalAddress?.districtName?.trim(),
          thirdIntermediaryAccountNumber: thirdIntermediary?.accountNumber?.identifierValue,
          thirdIntermediaryIBAN: thirdIntermediary?.iban?.identifierValue,
          thirdIntermediaryBIC: thirdIntermediary?.bic?.identifierValue,
          thirdIntermediaryClearingSysId: thirdIntermediary?.clearingSystemBankId?.identifierValue,

          // Recipient Account, Name and Address
          recipient:
            response.sloaInfo?.isThirdParty
              ? 'thirdParty'
              : 'firstParty',
          // Use SLOA flow wont have the checkbox checked even though the SLOA created has Escrow
          isEscrow: false,
          isISOCompliant: response?.sloaInfo?.isISOCompliant,
          receivingAccountHolderName:
            recipient?.name,
          receivingAccountHolderAccountNumber: recipient?.identifierList[0]?.identifierValue,
          receivingAccountHolderConcatenatedAddress:
            recipient?.postalAddress?.buildingNumber?.trim(),
          receivingAccountHolderCountry: response.sloaInfo?.isISOCompliant ?
            recipient?.postalAddress?.country?.trim() : recipient?.legacy?.country?.trim(),
          receivingAccountHolderIdentifierList:
            recipient?.identifierList,

          receivingAccountHolderStreetNumber: recipient?.postalAddress?.buildingNumber?.trim(),
          receivingAccountHolderStreetName: recipient?.postalAddress?.streetName?.trim(),
          receivingAccountHolderApartmentSuiteEtc: recipient?.postalAddress?.room?.trim(),
          receivingAccountHolderCity: recipient?.postalAddress?.townName?.trim(),
          receivingAccountHolderState: recipient?.postalAddress?.countrySubDivision?.trim(),
          receivingAccountHolderZip: recipient?.postalAddress?.postCode?.trim(),
          receivingAccountHolderDepartment: recipient?.postalAddress?.department?.trim(),
          receivingAccountHolderBuildingName: recipient?.postalAddress?.buildingName?.trim(),
          receivingAccountHolderFloor: recipient?.postalAddress?.floor?.trim(),
          receivingAccountHolderTownLocationName: recipient?.postalAddress?.townLocationName?.trim(),
          receivingAccountHolderDistrict: recipient?.postalAddress?.districtName?.trim(),
          receivingAccountHolderName1: recipient?.legacy?.name1?.trim(),
          receivingAccountHolderName2: recipient?.legacy?.name2?.trim(),
          receivingAccountHolderAddress1: recipient?.legacy?.address1?.trim(),
          receivingAccountHolderAddress2: recipient?.legacy?.address2?.trim(),

          toAccount: recipient?.identifierList[0]?.identifierValue,

          payeeName: response.beneficiaryFields?.pickList?.modifiedPayees[0]?.payeeNames[0]?.name ?? '',


          // TRANSMISSION NOTES with IRS field mapping
          transmissionNotes: response.sloaInfo?.isIrsAba ? response.sloaInfo?.taxRemittance?.additionalInfo: response.sloaInfo?.notesText[0],

          //creditToAddress Applicable only 3 Step and 4 Step Wire Flow not 1 and 2 Step Wire Flow
          creditToAddress: response.sloaInfo?.isoFinancialInstitutions?.length > 2 ? response.sloaInfo?.wiresBankDetails?.creditToAddress[0] : '',
          creditToAddressCont: response.sloaInfo?.isoFinancialInstitutions?.length > 2 ? response.sloaInfo?.wiresBankDetails?.creditToAddress[1] : '',
          creditToName: response.sloaInfo?.isoFinancialInstitutions?.length > 2 ? response.sloaInfo?.wiresBankDetails?.creditToName[0] : '',
          creditToNameCont: response.sloaInfo?.isoFinancialInstitutions?.length > 2 ? response.sloaInfo?.wiresBankDetails?.creditToName[1] : '',
          creditToCountry: response.sloaInfo?.isoFinancialInstitutions?.length > 2 ? response.sloaInfo?.wiresBankDetails?.creditToCountry : '',
          creditToPhone: response.sloaInfo?.isoFinancialInstitutions?.length > 2 ? response.sloaInfo?.wiresBankDetails?.creditToPhone : '',
          creditToAccountNumber: response.sloaInfo?.isoFinancialInstitutions?.length > 2 ? response.sloaInfo?.wiresBankDetails?.creditToAccountNumber : '',

        } as WireTransactionDetails,
      };
    }
  ),
  on(TransactionFormActions.templatePrefilledFlowStarted,
    (state, {response, mapRecipientFromDraft}) => {
      const percentFormatPipe = new FormatPercentPipe();
      const datePipe: DatePipe = new DatePipe('en-US');
      var transactionForm = {
        ...state,
        transactionDetails: {
          ...state.transactionDetails,
          amount: amountFormatter.format(response.transactionAmount),
          amountType: response.draftObject.iraTransactionRequest.isGrossAmt ? 'gross' : 'net',

          // RECEIVING BANK DETAILS SECTION
          aba: response.draftObject.beneficiaryBankId,
          bankID: response.draftObject.beneficiaryBankId,
          receivingBankName1: response.draftObject.beneficiaryBank.namePartOne,
          receivingBankName2: response.draftObject.beneficiaryBank.namePartTwo,
          receivingBankAddress1: response.draftObject.beneficiaryBank.addressLineOne,
          receivingBankAddress2: response.draftObject.beneficiaryBank.addressLineTwo,
          receivingBankCountry: response.draftObject.beneficiaryBank.countryCode,
          bankPhoneNumber: response.draftObject.beneficiaryBank.phoneNumber,

          // INTERMEDIARY SECTION
          intermediarySelection: !!response.draftObject.intermediary?.id ? 'yes' : 'no',
          intermediaryAccountNumber: response.draftObject?.intermediary?.id,
          intermediaryName: response.draftObject.isIsoCompliant ? (response.draftObject?.intermediary?.mailingDetails?.name ?? '' ):
            response.draftObject?.intermediary?.mailingDetails.namePartOne,
          intermediaryNameCont:
            response.draftObject?.intermediary?.mailingDetails.namePartTwo,
          intermediaryAddress:
            response.draftObject?.intermediary?.mailingDetails.addressLineOne,
          intermediaryAddressCont:
            response.draftObject?.intermediary?.mailingDetails.addressLineTwo,
          intermediaryCountry:
            response.draftObject?.intermediary?.mailingDetails.countryCode,
          intermediaryPhone:
            response.draftObject?.intermediary?.mailingDetails.phoneNumber,

          // RECIPIENT
          recipient: response.draftObject.isThirdParty ? 'thirdParty' : 'firstParty',
          isEscrow: response.draftObject.isEscrow,
          receivingAccountHolderName1:
            response.draftObject?.beneficiary?.namePartOne.trim(),
          receivingAccountHolderName2:
            response.draftObject?.beneficiary?.namePartTwo.trim(),
          receivingAccountHolderAddress1:
            response.draftObject?.beneficiary?.addressLineOne.trim(),
          receivingAccountHolderAddress2:
            response.draftObject?.beneficiary?.addressLineTwo.trim(),
          receivingAccountHolderCountry:
            response.draftObject?.beneficiary?.countryCode,
          toAccount: response.draftObject?.beneficiaryAccount,

          // Credit to
          creditToSelection: !!response.draftObject.creditTo.id ? yesNo.yes : yesNo.no,
          creditToAccountNumber: response.draftObject.creditTo.id,
          creditToName: response.draftObject.creditTo.mailingDetails.namePartOne,
          creditToNameCont:  response.draftObject.creditTo.mailingDetails.namePartTwo,
          creditToAddress:  response.draftObject.creditTo.mailingDetails.addressLineOne,
          creditToAddressCont:  response.draftObject.creditTo.mailingDetails.addressLineTwo,
          creditToCountry:  response.draftObject.creditTo.mailingDetails.countryCode,
          creditToPhone:  response.draftObject.creditTo.mailingDetails.phoneNumber,

          // Tax withholding fields
          taxWithholdingsFormGroup: {
            ...state.transactionDetails.taxWithholdingsFormGroup,
            fedTaxPercent: percentFormatPipe.transform(response.draftObject.iraTransactionRequest.fedTaxPercentage.toString()),
            stateTaxPercent: percentFormatPipe.transform(response.draftObject.iraTransactionRequest.stateTaxPercentage.toString()),
            distributionReason: response.draftObject.iraTransactionRequest.distributionReason.description
          },

          //ISO Intermediary
          intermediaryStreetNumber: response.draftObject?.intermediary?.mailingDetails?.buildingNumber ?? '',
          intermediaryStreetName: response.draftObject?.intermediary?.mailingDetails?.streetName ?? '',
          intermediaryApartmentSuiteEtc: response.draftObject?.intermediary?.mailingDetails?.room ?? '',
          intermediaryTownLocationName: response.draftObject?.intermediary?.mailingDetails?.townLocationName ?? '',
          intermediaryCity: response.draftObject?.intermediary?.mailingDetails?.townName ?? '',
          intermediaryDistrict: response.draftObject?.intermediary?.mailingDetails?.districtName ?? '',
          intermediaryState: response.draftObject?.intermediary?.mailingDetails?.countrySubDivision ?? '',
          intermediaryZip: response.draftObject?.intermediary?.mailingDetails?.zipCode ?? '',
          intermediaryDepartment: response.draftObject?.intermediary?.mailingDetails?.department ?? '',
          intermediaryBuildingName: response.draftObject?.intermediary?.mailingDetails?.buildingName ?? '',
          intermediaryFloor: response.draftObject?.intermediary?.mailingDetails?.floor ?? '',

          // IRS information
          taxPayerId: response.draftObject?.taxRemittance?.taxId ?? '',
          taxTypeCode: response.draftObject?.taxRemittance?.taxTypeCode ?? '',
          taxMonth: response.draftObject?.taxRemittance?.taxMonthCode ?? '',
          taxYear: response.draftObject?.taxRemittance?.taxYear ?? '',

          // Note
          transmissionNotes: !response.draftObject?.isIrsAba ? response.draftObject.note : response.draftObject?.taxRemittance?.additionalInfo,

        } as WireTransactionDetails
      };

      if (mapRecipientFromDraft) {
        transactionForm.transactionDetails.receivingAccountHolderName = response.draftObject?.beneficiary?.name ?? '';
        transactionForm.transactionDetails.receivingAccountHolderStreetName = response.draftObject?.beneficiary?.streetName ?? '';
        transactionForm.transactionDetails.receivingAccountHolderStreetNumber = response.draftObject?.beneficiary?.buildingNumber ?? '';
        transactionForm.transactionDetails.receivingAccountHolderApartmentSuiteEtc = response.draftObject?.beneficiary?.room ?? '';
        transactionForm.transactionDetails.receivingAccountHolderCity = response.draftObject?.beneficiary?.townName ?? '';
        transactionForm.transactionDetails.receivingAccountHolderState = response.draftObject?.beneficiary?.countrySubDivision ?? '';
        transactionForm.transactionDetails.receivingAccountHolderZip = response.draftObject?.beneficiary?.zipCode ?? '';
        transactionForm.transactionDetails.receivingAccountHolderDepartment = response.draftObject?.beneficiary?.department ?? '';
        transactionForm.transactionDetails.receivingAccountHolderBuildingName = response.draftObject?.beneficiary?.buildingName ?? '';
        transactionForm.transactionDetails.receivingAccountHolderFloor = response.draftObject?.beneficiary?.floor ?? '';
        transactionForm.transactionDetails.receivingAccountHolderTownLocationName = response.draftObject?.beneficiary?.townLocationName ?? '';
        transactionForm.transactionDetails.receivingAccountHolderDistrict = response.draftObject?.beneficiary?.districtName ?? '';
        if (response.draftObject?.isIsoCompliant) {
          transactionForm.transactionDetails.name = response.draftObject?.beneficiary?.name ?? '';
        } else {
          transactionForm.transactionDetails.name = response.draftObject?.beneficiary?.namePartOne + ' ' + response.draftObject?.beneficiary?.namePartTwo;
        }
      }

      return transactionForm;
    }
  ),
  on(CheckActions.inputCallSuccessful,
    (state, response) => {
      const percentFormatPipe = new FormatPercentPipe();
      const { checkInputResponse } = response;
      const isFirstPartyModified = checkInputResponse?.isFirstPartyModified ?? false;
      const modifiedPayeeDetails = checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.payeeDetails;
      const normalizedDefaultPayee = isFirstPartyModified
        ? [
            modifiedPayeeDetails?.payeeName1 ?? '',
            modifiedPayeeDetails?.payeeName2 ?? '',
            modifiedPayeeDetails?.payeeName3 ?? ''
          ].filter((payeeName) => !!payeeName?.trim())
        : checkInputResponse?.cashieringCheckInputDetails?.cashieringPayeeDetails?.defaultPayee;
      return {
        ...state,
        loading: false,
        serviceError: initialTransactionFormServiceError,
        showCashieringIRAAgreement: checkInputResponse?.iraaccount && !checkInputResponse?.isCashieringIRAAgreementOpened,
        transactionDetails: {
          ...state.transactionDetails,
          trustIncomePercent: checkInputResponse ? percentFormatPipe.transform(checkInputResponse.trustIncmPct) : '',
          trustPrincipalPercent: checkInputResponse ? percentFormatPipe.transform(checkInputResponse.trustPrinPct) : '',
          payeeType: checkInputResponse?.isThirdParty ? 'thirdParty' : 'firstParty',
          defaultPayee: normalizedDefaultPayee,
          modifiedPayeeCheckbox: isFirstPartyModified,
          modifiedPayeeName1: modifiedPayeeDetails?.payeeName1 ?? '',
          modifiedPayeeName2: modifiedPayeeDetails?.payeeName2 ?? '',
          modifiedPayeeName3: modifiedPayeeDetails?.payeeName3 ?? '',
          payeeName: isFirstPartyModified
            ? (modifiedPayeeDetails?.payeeName1 ?? "")
            : normalizedDefaultPayee?.length > 0
              ? normalizedDefaultPayee[0]
              : "",
          recipient: checkRecipientTypeDict[checkInputResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.recipientAddressType as keyof typeof checkRecipientTypeDict] as CheckAbbreviatedRecipientTypes,
          processDate: `${checkInputResponse.processDateMM}/${checkInputResponse.processDateDD}/${checkInputResponse.processDateYYYY}`,
          checkReason: checkInputResponse?.withdrawalReasonCode,
          memoLine2: checkInputResponse?.memoLine2,
          checkMemo: checkInputResponse?.memo,
          amount: checkInputResponse?.amount,
          deliveryMethod: checkInputResponse?.deliveryMethodCode == 'M' ? 'standard' : 'overnight',
          frequency: checkInputResponse?.type == 'onetimeCheck' ? 'onetime' : 'recurring',
          distributionReasonCode: checkInputResponse?.distributionReasonCode

        } as CheckTransactionDetails
      }
    }
  ),
  on(AchActions.inputCallSuccessful, (state, response) => {
    if (response.achInfo.trustIncomePercent !== '') {
      return {
        ...state,
        loading: false,
        serviceError: initialTransactionFormServiceError,
        showCashieringIRAAgreement: response.achInfo.isIraTransaction && !response.achInfo.iraFields?.isCashieringIraAgreementOpened,
        transactionDetails: {
          ...state.transactionDetails,
          trustIncomePercent: response.achInfo.trustIncomePercent,
          trustPrincipalPercent: response.achInfo.trustPrincipalPercent
        }
      }
    }
    return {
      ...state,
      loading: false,
      serviceError: initialTransactionFormServiceError,
      showCashieringIRAAgreement: response.achInfo.isIraTransaction && !response.achInfo.iraFields?.isCashieringIraAgreementOpened,
    }
  }),
  on(InternalTransferActions.loadToAccountSuccess,
    InternalTransfersActions.itLoadToAccountSuccess,
     (state, response) => {
    const transactionDetails = state.transactionDetails as ItTransactionDetails;
    return {
      ...state,
      loading: false,
      serviceError: initialTransactionFormServiceError,
      showCashieringIRAAgreement: response.internalTransferInputResponse.iraFields?.isIraTransaction &&
        !response.internalTransferInputResponse.iraFields?.isCashieringIRAAgreementOpened,
      transactionDetails: {
        ...transactionDetails,
        trustIncomePercent: response.internalTransferInputResponse.trustFields.trustIncmPct !== '' ? response.internalTransferInputResponse.trustFields.trustIncmPct : '100%',
        trustPrincipalPercent: response.internalTransferInputResponse.trustFields.trustPrinPct !== '' ? response.internalTransferInputResponse.trustFields.trustPrinPct : '0%',
        trustIncomeToPercent: response.internalTransferInputResponse.toAccountInfo.toIncome !== '' ? response.internalTransferInputResponse.toAccountInfo.toIncome : '100%',
        trustPrincipalToPercent: response.internalTransferInputResponse.toAccountInfo.toPrincipal !== '' ? response.internalTransferInputResponse.toAccountInfo.toPrincipal : '0%',
        taxWithholdingsFormGroup: {
          ...transactionDetails.taxWithholdingsFormGroup,
          distributionReason: ''
        }
      }
    }
  }),
  on(InternalTransferActions.loadToAccountFinishedWithError,
    InternalTransferActions.verifyCallFinishedWithError,
    InternalTransfersActions.verifyListCallFinishedWithError,
    CheckActions.verifyCallFinishedWithError,
    WireActions.callWireTemplatesGetListFailed, (state, _response) => {
      return {
        ...state,
        loading: false
      }
    }),
  on(InternalTransfersActions.verifyListCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferListVerify,
        message: err.message,
        hasError: !err.ok
      },
    }
  }),
  on(
    TransactionFormActions.callAchVerify,
    TransactionFormActions.callAchAddProfileVerify,
    TransactionFormActions.callInternalTransferVerify,
    TransactionFormActions.callInternalTransferListVerify,
    TransactionFormActions.callWiresVerifyV3,
    TransactionFormActions.callWireSloaVerifyV3FromCreateTransaction,
    TransactionFormActions.callOnetimeCheckVerify,
    TransactionFormActions.callRecurringCheckVerify,
    (state) => {
      return {
        ...state,
        loading: true,
        serviceError: initialTransactionFormServiceError
      };
    }
  ),
  on(AchActions.verifyCallSuccessful, (state, {response}) => {
    let amount = '';
    const modifiedState = {
      ...state,
      loading: false,
      serviceError: initialTransactionFormServiceError,
    };

    if (response?.iraFields?.grossNetIndicator === 'N') {
      amount = response.transferAmount;
    } else {
      amount = response.totalWithdrawalAmount;
    }

    if (response.messageFields.hasError) {
      modifiedState.error = response.messageFields.hasError
        ? response.messageFields.errorList[0].messageText
        : '';
      modifiedState.hasError = response.messageFields.hasError;
    } else {
      modifiedState.error = '';
      modifiedState.hasError = false;
      modifiedState.transactionDetails = {
        ...state.transactionDetails,
        amount: amount,
        transferDirection: response.fundsDirection,
      } as AchTransactionDetails;
    }

    return modifiedState;
  }),
  on(
    StandingAuthorizationsActions.loadSloasSuccess,
    StandingAuthorizationsActions.loadSloasFailure,
    TransactionFormActions.editLoading,
    (state) => {
      return {
        ...state,
        loading: false,
      };
    }
  ),
  on(AchActions.inputCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.moneylinkInput,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(AchActions.verifyCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.moneylinkVerify,
        message: err.message,
        hasError: !err.ok,
        errorList: err.error?.errors ?? []
      },
    };
  }),
  on(AchActions.acknowledgeCallFail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.moneylinkAcknowledge,
        message: err.message,
        hasError: !err.ok,
        errorList: err.error?.errors ?? []
      },
    };
  }),
  on(InternalTransferActions.loadToAccountFail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferInput,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(InternalTransferActions.verifyCallFail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferVerify,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(InternalTransferActions.acknowledgeCallFail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferAcknowledge,
        message: err.message,
        hasError: !err.ok,
        errorList: err.error?.errors ?? []
      },
    };
  }),
  on(CheckActions.acknowledgeCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkAcknowledge,
        message: err.message,
        errorList: err.error?.errors ?? [],
        hasError: !err.ok
      },
    };
  }),
  on(TransactionDetailsActions.loadWithdrawalDateFailure, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.withdrawalDate,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(StandingAuthorizationsActions.loadSloasFailure, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.standingAuthorizations,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(AccountInfoActions.loadTransactionsFailure, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.overview,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(
    TransactionFormActions.initializeServiceError,
    InternalTransferActions.clearToAccount,
    (state) => {
      return {
        ...state,
        serviceError: initialTransactionFormServiceError,
      };
    }
  ),
  on(TransactionDetailsActions.loadWithdrawalDateSuccess, (state, value) => {
    if (isAchForm(state)) {
      let finalState = {
        ...state,
        transactionDetails: {
          ...state.transactionDetails,
          withdrawalDate: value.initiationDate,
        }
      } as AchForm;
      if (state.extraPrompts.transferDirection === 'incoming') {
        //Will need both fundsAvailableToTrade to display to UI and settlementDate to send to cashiering(ProcessMoneyLinkSI -1 day)
        finalState.transactionDetails.fundsAvailableToTrade = value.fundsCreditDate;
        finalState.transactionDetails.settlementDate = value.settlementDate;
      }
      return finalState;
    }
    return { ...state };
  }),
  on(
    ConfirmationPageActions.createAnotherRequestClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    TransactionFormActions.cancelClicked,
    TransactionFormActions.resetTransactionForm,
    AccountInfoActions.updateSubAccount,
    ReviewPageActions.cancelClicked,
    InitReleaseActions.getInitReleaseList,
    () => {
      return initialTransactionFormState;
    }
  ),
  on(WireActions.wireDetailsCallFailed, (state, err) => {
    return {
      ...state,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireDetails,
        message: err.message,
        hasError: !err.ok
      }
    }
  }),
  on(WireActions.callWireDetails, state => {
    return {
      ...state,
      serviceError: {
        ...state.serviceError,
        status: 0,
        url: '',
        name: '',
        message: '',
        hasError: false
      }
    }
  }),
  
  on(WireActions.wireInputFailedV3, WireActions.wireInputV3TemplateFlowFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireInputV3,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(CheckActions.verifyCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      hasError: true,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkVerify,
        message: err.error?.errors[0]?.detail ?? err.message,
        errorList: err.error?.errors ?? [],
        hasError: !err.ok
      },
    };
  }),
  on(CheckActions.inputCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkInput,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),

  on(WireActions.verifyV3CallFailed, WireActions.callWireSloaVerifyV3FromCreateTransactionFailed, (state, err) => {
    let errMsg = err.message;
    if (err.status === 400 && err.error?.errors && err.error.errors[0]) {
      errMsg = err.error.errors[0].detail;
    }
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireVerifyV3,
        message: errMsg,
        errorList: err.error?.errors ?? [],
        hasError: !err.ok
      },
    };
  }),
  on(WireActions.acknowledgeCallV3Fail, (state, err) => {
    return {
      ...state,
      loading: false,
      acknowledgeCallV3FailedForToggleOn: true,
      acknowledgeCallV3Failed: true,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireAcknowledgeV3,
        message: err.message,
        errorList: err.error?.errors ?? [],
        hasError: !err.ok
      },
    };
  }),
  on(
    TransactionsTableActions.editTransactionClicked,
    ViewPageActions.editButtonClicked,
    state => {
      return {
        ...state,
        prefilledFlow: {
          state: prefilledFlowStates.started,
          name: prefilledFlowNames.editTable
        },
      }
    }
  ),
  on(ViewPageActions.backButtonClicked,
    ViewPageActions.backButtonClickedFromMsv,
    (state) => {
    return {
      ...state,
      serviceError: initialTransactionFormServiceError,
    };
  }),
  on(StandingAuthorizationsTableActions.startTransactionClicked, state => {
    return {
      ...state,
      isFromEditSubmittedTransaction: false
    }
  }),
  on(TransactionsTableActions.editInProgress, (state) => {
    return {
      ...state,
      prefilledFlow: {
        state: prefilledFlowStates.progress,
        name: prefilledFlowNames.editTable
      },
    }
  }),
  on(
    StandingAuthorizationsTableActions.startTransactionClicked,
    SharedActions.showEditingRestrictedModal,
    TransactionsTableActions.loadViewPage,
    state => {
      return {
        ...state,
        prefilledFlow: {
          ...initialTransactionFormState.prefilledFlow
        }
      }
    }),
  on(CheckActions.inquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      prefilledFlow: {
        ...state.prefilledFlow,
        state: prefilledFlowStates.complete
      },
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkInquiry,
        message: err.message,
        hasError: !err.ok
      }
    }
  }),
  on(InternalTransferActions.inquiryCallFail, (state, err) => {
    return {
      ...state,
      loading: false,
      prefilledFlow: {
        ...state.prefilledFlow,
        state: prefilledFlowStates.complete
      },
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferInquiry,
        message: err.message,
        hasError: !err.ok
      }
    }
  }),
  on(InternalTransferActions.inquiryCallSuccessful, (state, response) => {
    return {
      ...state,
      transactionType: transactionTypes.internalTransfer
    }
  }),
  on(CheckActions.inquiryCallSuccessful, (state) => {
    return {
      ...state,
      transactionType: transactionTypes.check
    }
  }),
  on(PtrsActions.transInquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      prefilledFlow: {
        ...state.prefilledFlow,
        state: prefilledFlowStates.complete
      },
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.transInquiry,
        message: err.message,
        hasError: !err.ok
      }
    }
  }),
  on(TransactionFormActions.TransactionTypeSelected, (state) => {
    return {
      ...state,
      createTransactionInProgress: true
    }
  }),
  on(TransactionFormActions.transactionTypeChanged, (state, { transactionType }) => {
    return {
      ...state,
      createTransactionInProgress: !!transactionType
    }
  }),
  on(InitReleaseActions.initReleaseByDraftIdSuccessful, (state, { response, isSloa } ) => {
    if (isSloa) return state;
    if (response.transactionType === 'journal') {
      return {
        ...state,
        ...mapToTransactionFormJournals(response),
        serviceError: initialTransactionFormServiceError
      }
    }
    if (response.transactionType === transactionTypes.wire) {
      const transactionDetails = state.transactionDetails as WireTransactionDetails;
      const standingAuthId = [response.draftObject.cashieringRequestId, response.draftObject.cashieringSecRequestId].filter(x => !!x).join('-');
      return {
        ...state,
        transactionType: response.transactionType as transactionTypes,
        transactionDetails: {
          ...transactionDetails,
          ...mapWireTxnDetailsFromInitRelease(response)
        },
        extraPrompts: {
          standingAuthId: standingAuthId ? `w-${standingAuthId}` : '',
          requestingAccountHolderId: response.draftObject.requestingAccountHolder,
          useStandingAuth: response.draftObject.bSLOATxn ? 'bundled': response.draftObject.isFromSLOA ? 'yes' : 'no',
          useBundledStandingAuth: response.draftObject.bSLOATxn && response.draftObject.isFromSLOA ? 'yes' : 'no'
        } as WireExtraPrompts
      }
    }
    if (response.transactionType === 'moneylink') {
      return {
        ...state,
        ...mapAchTxnDetailsFromInitRelease(response)
      }
    }
    if (response.transactionType === transactionTypes.check) {
      const transactionDetails = state.transactionDetails as CheckTransactionDetails;

      return {
        ...state,
        transactionType: response.transactionType as transactionTypes,
        transactionDetails: {
          ...transactionDetails,
          ...mapCheckTxnDetailsFromInitRelease(response)
        },
        extraPrompts: {
          useStandingAuth: response.draftObject.bSLOATxn ? 'bundled' : response.draftObject.isFromSLOA ? 'yes' : 'no',
          useBundledStandingAuth: response.draftObject.bSLOATxn && response.draftObject.isFromSLOA ? 'yes' :
            response.draftObject.bSLOATxn && response.draftObject.formType === formType.formTypeRecCheck ? 'recurring' : 'no',
          frequency: response.draftObject.IsRecurringTransaction ? 'recurring' : 'onetime',
          standingAuthId: response.draftObject.cashieringReqId?.toString() ?? ''
        } as ChecksExtraPrompts
      }
    }
    return { ...state };
  }),
  on(InitReleaseActions.initReleaseByDraftIdFailed, (state, err) => {
    return {
      ...state,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.initReleaseByDraftId,
        message: err.message,
        hasError: !err.ok
      }
    }
  })
);

export function mapToTransactionFormJournals(draft: DraftListAttributes): TransactionForm {
  const titleCasePipe = new TitleCasePipe();
  let rawToAccountId = draft.draftObject.toAccountId?.toString() ?? '';
  let toAccountId = rawToAccountId.slice(0, 4) + '-' + rawToAccountId.slice(4);
  let isUseStandingAuth = draft.draftObject.bsloaTransaction ? 'bundled': draft.draftObject.isSloaPopupSelected ? 'yes' : 'no'
  return {
    ...initialTransactionFormState,
    transactionType: transactionTypes.internalTransfer,
    extraPrompts: {
      useStandingAuth: isUseStandingAuth,
      useBundledStandingAuth: (draft.draftObject.bsloaTransaction && draft.draftObject.isSloaPopupSelected) ? 'yes' : (draft.draftObject.bsloaTransaction && draft.draftObject.isRecurringTransaction) ? 'recurring' : draft.draftObject.bsloaTransaction ? 'no' : '',
      frequency: draft.draftObject.isRecurringTransaction ? 'recurring' : 'onetime',
      standingAuthId: isUseStandingAuth ? String('i-' + draft.draftObject.cashieringRequestId?.toString() + '-' + draft.draftObject.cashieringSecondRequestId?.toString()) : draft.draftObject.cashieringRequestId?.toString(),
      transferType: draft.draftObject.selectedPositionsCount ?? 0 > 0 ? +draft.draftObject.amount > 0 ? 'positionsCash' : 'positions' : 'cash'
    } as ItExtraPrompts,
    transactionDetails: {
      toAccount: toAccountId,
      processDate: draft.draftObject.processDate,
      amount: draft.draftObject.amount.toString(),
      amountType: draft.draftObject.amountType,
      taxWithholdingsFormGroup: {
        ...initialTransactionFormState.transactionDetails.taxWithholdingsFormGroup,
        fedTaxPercent: draft.draftObject.fedTaxAmount?.toString(),
        stateTaxPercent: draft.draftObject.stateTaxAmount?.toString(),
        distributionReason: (draft.draftObject.distributionReason as DistributionReason)?.description
      },
      recurringFieldsFormGroup: {
        recurringFrequency: (draft.draftObject.frequency as CodeAndDescription)?.code,
        endDate: draft.draftObject.endDate,
        secondProcessDate: draft.draftObject.process2ndDate,
        payIncome: draft.draftObject.payIncome?.code
      },
      iraContributionType: draft.draftObject.iraContributionType?.description,
      iraContributionTypeMap: [{
        code: draft.draftObject.iraContributionType?.code,
        subCode: draft.draftObject.iraContributionType?.subCode,
        year: draft.draftObject.iraContributionType?.year,
        description: draft.draftObject.iraContributionType?.description
      }],
      trustIncomePercent: draft.draftObject.trustIncmPct,
      trustPrincipalPercent: draft.draftObject.trustPrinPct,
      trustIncomeToPercent: draft.draftObject.toIncome ? `${draft.draftObject.toIncome}%` : '',
      trustPrincipalToPercent: draft.draftObject.toPrincipal ? `${draft.draftObject.toPrincipal}%` : '',
      trustDepositCode: draft.draftObject.depositTaxCode,
      trustTaxCategory: draft.draftObject.trustTaxCategory,
      trustTaxCode: draft.draftObject.trustTaxCode,
      trustPaidFor: titleCasePipe.transform(`${draft.draftObject.paidFor?.name} - ${draft.draftObject.paidFor?.role}`),
      trustComment: draft.draftObject.tranMemo,
      costBasisMethod: draft.draftObject?.selectedCostBasisMethod,
      // Multiple toAccount flow
      toAccounts: [
        {
          toAccount: toAccountId,
          iraDistributionType: (draft.draftObject.distributionReason as DistributionReason)?.description,
          iraContributionType: draft.draftObject.iraContributionType?.description,
          trustDepositCode: draft.draftObject.depositTaxCode,
          trustIncomeToPercent: draft.draftObject.toIncome && !isNaN(Number(draft.draftObject.toIncome)) ? `${draft.draftObject.toIncome}%` : '',
          trustPrincipalToPercent: draft.draftObject.toPrincipal && !isNaN(Number(draft.draftObject.toPrincipal)) ? `${draft.draftObject.toPrincipal}%` : ''
        }
      ],
      cashAmounts: [
        {
          toAccount: toAccountId,
          amount: draft.draftObject.amount.toString(),
          amountType: draft.draftObject.amountType,
        }
      ]
    } as ItTransactionDetails
  }
}

function mapWireTxnDetailsFromInitRelease(response: DraftListAttributes): WireTransactionDetails {
  const titleCasePipe = new TitleCasePipe();
  const draftObject = response.draftObject;
  const isIsoCompliant = !!response.draftObject.isIsoCompliant;
  const isIrsAba = draftObject.isIrsAba ?? draftObject.IsIrsAba;
  const taxRemittance = draftObject.taxRemittance ?? draftObject.TaxRemittance;
  let fedTaxPercent: string = draftObject.fedTaxAmount.toString();
  let stateTaxPercent: string = draftObject.stateTaxAmount.toString();
  let isthirdparty:boolean = draftObject.payeeType === "payeeThirdParty" || draftObject.payeeType === "payeeEscrow";

  let intermediaryPhoneNumber = [draftObject.furCreditPhone1, draftObject.furCreditPhone2, draftObject.furCreditPhone3].filter(x => x).join('-');
  intermediaryPhoneNumber = draftObject.furCreditPhone4 ? `${intermediaryPhoneNumber} Ext${draftObject.furCreditPhone4}` : intermediaryPhoneNumber;

  let receivingBankPhoneNumber = [draftObject.recBankPhone1, draftObject.recBankPhone2, draftObject.recBankPhone3].filter(x => x).join('-');
  receivingBankPhoneNumber = draftObject.recBankPhone4 ? `${receivingBankPhoneNumber} Ext${draftObject.recBankPhone4}` : receivingBankPhoneNumber;

  return {
    name: isIsoCompliant ? (draftObject.beneficiaryInfo?.name || draftObject.recipient?.name || '') : '',
    aba: draftObject.recBankAba ?? '',
    recipient: isthirdparty ? 'thirdParty' : 'firstParty',
    receivingBankCountry: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.country || draftObject.recipientBank?.postalAddress?.country || '') : draftObject.recBankCountry ?? '',
    toAccount: draftObject.beneAccount ?? '',
    selectedDate: draftObject.processDate ?? '',
    amount: response.transactionAmount.toString(),
    isEscrow: draftObject.payeeType === "payeeEscrow",
    remittanceInformation: '',
    transmissionNotes: !isIrsAba ? (draftObject.note ?? draftObject.Note ?? '') : taxRemittance?.additionalInfo ?? '',
    trustComment: draftObject.tranMemo ?? '',
    trustIncomePercent: draftObject.trustIncmPct ?? '',
    trustPrincipalPercent: draftObject.trustPrinPct ?? '',
    trustTaxCode: draftObject.trustTaxCode ?? '',
    trustTaxCategory: draftObject.trustTaxCategory,
    trustPaidFor: titleCasePipe.transform(`${draftObject.paidFor?.name} - ${draftObject.paidFor?.role}`),
    amountType: draftObject.amountType ?? '',
    intermediarySelection: draftObject.canDisplayFurCreditInfo ? 'yes' : 'no',
    intermediaryIdentifierList: isIsoCompliant ? draftObject.firstIntermediary?.identifierList ?? [] : [],
    intermediaryName: isIsoCompliant ? draftObject.firstIntermediary?.name ?? '' : draftObject.furCreditName1 ?? '',
    intermediaryNameCont: isIsoCompliant ? draftObject.firstIntermediary?.legacy.name2 ?? '' : draftObject.furCreditName2 ?? '',
    intermediaryAccountNumber: isIsoCompliant ? draftObject.firstIntermediary?.identifierList?.filter(x => x.identifierType === "Account")[0]?.identifierValue ?? '' : draftObject.furCreditAccount ?? '',
    intermediaryAddress: isIsoCompliant ? draftObject.firstIntermediary?.legacy.address1 ?? '' : draftObject.furCreditAddress1 ?? '',
    intermediaryAddressCont: isIsoCompliant ? draftObject.firstIntermediary?.legacy.address2 ?? '' : draftObject.furCreditAddress2 ?? '',
    intermediaryCountry: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress.country ?? '' : draftObject.furCountryCd ?? '',
    intermediaryPhone: isIsoCompliant ? draftObject.firstIntermediary?.phone ?? '' : intermediaryPhoneNumber,
    intermediaryStreetNumber: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.buildingNumber ?? '' : '',
    intermediaryStreetName: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.streetName ?? '' : '',
    intermediaryApartmentSuiteEtc: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.room ?? '' : '',
    intermediaryConcatenatedAddress: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress.concatenatedAddress ?? '' : '',
    intermediaryCity: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.townName ?? '' : '',
    intermediaryState: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.countrySubDivision ?? '' : '',
    intermediaryZip: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.postCode ?? '' : '',
    intermediaryDepartment: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.department ?? '' : '',
    intermediaryBuildingName: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.buildingName ?? '' : '',
    intermediaryFloor: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.floor ?? '' : '',
    intermediaryTownLocationName: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.townLocationName ?? '' : '',
    intermediaryDistrict: isIsoCompliant ? draftObject.firstIntermediary?.postalAddress?.districtName ?? '' : '',
    intermediaryIBAN: isIsoCompliant ? draftObject.firstIntermediary?.identifierList.filter(x => x.identifierType === "IBAN")[0]?.identifierValue : null, // set to null if not exists for correct component use
    intermediaryBIC: isIsoCompliant ? draftObject.firstIntermediary?.identifierList?.filter(x => x.identifierType === "BIC")[0]?.identifierValue ?? '' : '',
    intermediaryClearingSysId: isIsoCompliant ? draftObject.firstIntermediary?.identifierList?.filter(x => x.identifierType !== "Account" && x.identifierType !== "IBAN" && x.identifierType !== "BIC")[0]?.identifierValue ?? '' : '',

    // Second Intermediary - ISO Flow
    isThreeStep: isIsoCompliant ? draftObject.secondIntermediary != null && draftObject.thirdIntermediary == null : false,
    secondIntermediaryIdentifierList: isIsoCompliant ? draftObject.secondIntermediary?.identifierList ?? [] : [],
    secondIntermediaryName: isIsoCompliant ? draftObject.secondIntermediary?.name ?? '' : '',
    secondIntermediaryConcatenatedAddress: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.concatenatedAddress ?? '' : '',
    secondIntermediaryCountry: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.country ?? '' : '',
    secondIntermediaryAccountNumber: isIsoCompliant ? draftObject.secondIntermediary?.identifierList?.filter(x => x.identifierType === "Account")[0]?.identifierValue ?? '' : '',
    secondIntermediaryIBAN: isIsoCompliant ? draftObject.secondIntermediary?.identifierList?.filter(x => x.identifierType === "IBAN")[0]?.identifierValue ?? '' : '',
    secondIntermediaryBIC: isIsoCompliant ? draftObject.secondIntermediary?.identifierList?.filter(x => x.identifierType === "BIC")[0]?.identifierValue ?? '' : '',
    secondIntermediaryClearingSysId: isIsoCompliant ? draftObject.secondIntermediary?.identifierList?.filter(x => x.identifierType !== "Account" && x.identifierType !== "IBAN" && x.identifierType !== "BIC")[0]?.identifierValue ?? '' : '',
    secondIntermediaryStreetNumber: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.buildingNumber ?? '' : '',
    secondIntermediaryStreetName: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.streetName ?? '' : '',
    secondIntermediaryApartmentSuiteEtc: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.room ?? '' : '',
    secondIntermediaryCity: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.townName ?? '' : '',
    secondIntermediaryState: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.countrySubDivision ?? '' : '',
    secondIntermediaryZip: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.postCode ?? '' : '',
    secondIntermediaryDepartment: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.department ?? '' : '',
    secondIntermediaryBuildingName: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.buildingName ?? '' : '',
    secondIntermediaryFloor: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.floor ?? '' : '',
    secondIntermediaryTownLocationName: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.townLocationName ?? '' : '',
    secondIntermediaryDistrict: isIsoCompliant ? draftObject.secondIntermediary?.postalAddress?.districtName ?? '' : '',

    // Third Intermediary - ISO Flow
    isFourStep: isIsoCompliant ? draftObject.thirdIntermediary != null : false,
    thirdIntermediaryIdentifierList: isIsoCompliant ? draftObject.thirdIntermediary?.identifierList ?? [] : [],
    thirdIntermediaryName: isIsoCompliant ? draftObject.thirdIntermediary?.name ?? '' : '',
    thirdIntermediaryConcatenatedAddress: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.concatenatedAddress ?? '' : '',
    thirdIntermediaryCountry: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.country ?? '' : '',
    thirdIntermediaryAccountNumber: isIsoCompliant ? draftObject.thirdIntermediary?.identifierList?.filter(x => x.identifierType === "Account")[0]?.identifierValue ?? '' : '',
    thirdIntermediaryIBAN: isIsoCompliant ? draftObject.thirdIntermediary?.identifierList.filter(x => x.identifierType === "IBAN")[0]?.identifierValue ?? '' : '',
    thirdIntermediaryBIC: isIsoCompliant ? draftObject.thirdIntermediary?.identifierList.filter(x => x.identifierType === "BIC")[0]?.identifierValue ?? '' : '',
    thirdIntermediaryClearingSysId: isIsoCompliant ? draftObject.thirdIntermediary?.identifierList.filter(x => x.identifierType !== "Account" && x.identifierType !== "IBAN" && x.identifierType !== "BIC")[0]?.identifierValue ?? '' : '',
    thirdIntermediaryStreetNumber: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.buildingNumber ?? '' : '',
    thirdIntermediaryStreetName: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.streetName ?? '' : '',
    thirdIntermediaryApartmentSuiteEtc: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.room ?? '' : '',
    thirdIntermediaryCity: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.townName ?? '' : '',
    thirdIntermediaryState: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.countrySubDivision ?? '' : '',
    thirdIntermediaryZip: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.postCode ?? '' : '',
    thirdIntermediaryDepartment: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.department ?? '' : '',
    thirdIntermediaryBuildingName: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.buildingName ?? '' : '',
    thirdIntermediaryFloor: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.floor ?? '' : '',
    thirdIntermediaryTownLocationName: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.townLocationName ?? '' : '',
    thirdIntermediaryDistrict: isIsoCompliant ? draftObject.thirdIntermediary?.postalAddress?.districtName ?? '' : '',

    canDisplayIntermediaryPhone: false,
    creditToSelection: !!draftObject.furtherCredit2Account ? 'yes' : 'no',
    creditToName: draftObject.furtherCredit2Name1 ?? '',
    creditToNameCont: draftObject.furtherCredit2Name2 ?? '',
    creditToAccountNumber: draftObject.furtherCredit2Account ?? '',
    creditToAddress: draftObject.furtherCredit2Address1 ?? '',
    creditToAddressCont: draftObject.furtherCredit2Address2 ?? '',
    creditToCountry: draftObject.furtherCredit2CountryCd ?? '',
    creditToPhone: draftObject.furtherCredit2Phone1 ?? '',
    canDisplayCreditToPhone: !!draftObject.furtherCredit2Phone1,
    costBasisMethod: '',
    labelRefNum: '',
    refNum: '',
    requestedDate: draftObject?.originalRequestedDate ?? '',
    payeeName: `${draftObject.beneName1} ${draftObject.beneName2}`,

    // Beneficiary Info
    receivingAccountHolderName: isIsoCompliant ? (draftObject.beneficiaryInfo?.name || draftObject.recipient?.name || '') : `${draftObject.beneName1} ${draftObject.beneName2}`,
    receivingAccountHolderStreetName: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.streetName || draftObject.recipient?.postalAddress?.streetName || '') : '',
    receivingAccountHolderStreetNumber: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.buildingNumber || draftObject.recipient?.postalAddress?.buildingNumber || '') : '',
    receivingAccountHolderApartmentSuiteEtc: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.room || draftObject.recipient?.postalAddress?.room || '') : '',
    receivingAccountHolderCity: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.townName || draftObject.recipient?.postalAddress?.townName || '') : '',
    receivingAccountHolderState: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.countrySubDivision || draftObject.recipient?.postalAddress?.countrySubDivision || '') : '',
    receivingAccountHolderZip: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.postCode || draftObject.recipient?.postalAddress?.postCode || '') : '',
    receivingAccountHolderDepartment: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.department || draftObject.recipient?.postalAddress?.department || '') : '',
    receivingAccountHolderBuildingName: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.buildingName || draftObject.recipient?.postalAddress?.buildingName || '') : '',
    receivingAccountHolderFloor: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.floor || draftObject.recipient?.postalAddress?.floor || '') : '',
    receivingAccountHolderTownLocationName: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.townLocationName || draftObject.recipient?.postalAddress?.townLocationName || '') : '',
    receivingAccountHolderDistrict: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress?.districtName || draftObject.recipient?.postalAddress?.districtName || '') : '',
    receivingAccountHolderPhone: isIsoCompliant ? (draftObject.beneficiaryInfo?.phone || draftObject.recipient?.phone || '') : '',
    receivingAccountHolderIdentifierList: isIsoCompliant ? (draftObject.beneficiaryInfo?.identifierList || draftObject.recipient?.identifierList || []) : [],
    receivingAccountHolderConcatenatedAddress: isIsoCompliant ? (draftObject.beneficiaryInfo?.postalAddress.concatenatedAddress || draftObject.recipient?.postalAddress.concatenatedAddress || '') : "",
    receivingAccountHolderAccountNumber: isIsoCompliant ? draftObject.beneficiaryInfo?.identifierList.filter(x => x.identifierType === "Account")[0]?.identifierValue ?? '' : '',

    // Beneficiary Bank Info
    receivingBankIdentifierList: isIsoCompliant ? (draftObject.beneficairyBank?.identifierList || draftObject.recipientBank?.identifierList) ?? [] : [],
    receivingBankClearingSysId: isIsoCompliant ? draftObject.recipientBank?.clearingSystemBankId?.identifierValue ?? '' : "",
    receivingBankName: isIsoCompliant ? (draftObject.beneficairyBank?.name || draftObject.recipientBank?.name) ?? '' : "",
    receivingBankConcatenatedAddress: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.concatenatedAddress || draftObject.recipientBank?.postalAddress?.concatenatedAddress) ?? '' : "",
    receivingBankName1: draftObject.recBankName1 ?? '',
    receivingBankName2: draftObject.recBankName2 ?? '',
    receivingBankAddress1: draftObject.recBankAddress1 ?? '',
    receivingBankAddress2: draftObject.recBankAddress2 ?? '',
    bankPhoneNumber: isIsoCompliant ? (draftObject.beneficairyBank?.phone || draftObject.recipientBank?.phone) ?? '' : receivingBankPhoneNumber,
    receivingBankStreetNumber: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.buildingNumber || draftObject.recipientBank?.postalAddress?.buildingNumber) ?? '' : '',
    receivingBankStreetName: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.streetName || draftObject.recipientBank?.postalAddress?.streetName) ?? '' : '',
    receivingBankApartmentSuiteEtc: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.room || draftObject.recipientBank?.postalAddress?.room) ?? '' : '',
    receivingBankCity: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.townName || draftObject.recipientBank?.postalAddress?.townName) ?? '' : '',
    receivingBankState: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.countrySubDivision || draftObject.recipientBank?.postalAddress?.countrySubDivision) ?? '' : '',
    receivingBankZip: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.postCode || draftObject.recipientBank?.postalAddress?.postCode) ?? '' : '',
    receivingBankDepartment: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.department || draftObject.recipientBank?.postalAddress?.department) ?? '' : '',
    receivingBankBuildingName: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.buildingName || draftObject.recipientBank?.postalAddress?.buildingName) ?? '' : '',
    receivingBankFloor: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.floor || draftObject.recipientBank?.postalAddress?.floor) ?? '' : '',
    receivingBankTownLocationName: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.townLocationName || draftObject.recipientBank?.postalAddress?.townLocationName) ?? '' : '',
    receivingBankDistrict: isIsoCompliant ? (draftObject.beneficairyBank?.postalAddress?.districtName || draftObject.recipientBank?.postalAddress?.districtName) ?? '' : '',
    canDisplayReceivingBankPhone: isIsoCompliant ? !!(draftObject.beneficairyBank?.phone || draftObject.recipientBank?.phone) : false,
    bankID: draftObject.recBankAba ?? '',
    receivingAccountHolderName1: draftObject.beneName1 ?? '',
    receivingAccountHolderName2: draftObject.beneName2 ?? '',
    receivingAccountHolderAddress1: draftObject.beneAddress1 ?? '',
    receivingAccountHolderAddress2: draftObject.beneAddress2 ?? '',
    receivingAccountHolderCountry: draftObject.beneCountry ?? '',
    countryCode: draftObject.recBankCountry ?? '',
    taxWithholdingsFormGroup: {
      fedTaxPercent: fedTaxPercent + '%',
      fedTaxAmount: fedTaxPercent,
      stateTaxPercent: stateTaxPercent + '%',
      stateTaxAmount: stateTaxPercent,
      distributionReason: draftObject.distributionReasonDescription ?? ''
    },
    isISOCompliant: isIsoCompliant,
    taxPayerId: taxRemittance?.taxId ?? '',
    taxTypeCode: taxRemittance?.taxTypeCode ?? '',
    taxMonth: taxRemittance?.taxMonthCode ?? '',
    taxYear: taxRemittance?.taxYear ?? '',
  }
}

function mapAchTxnDetailsFromInitRelease(response: DraftListAttributes): TransactionForm {
  const titleCasePipe = new TitleCasePipe();
  const datePipe = new DatePipe('en-US');

  // Needed for cross-ui compatibility since drafts created from legacy doesn't have grossNetIndicator
  let grossNetIndicator = response.draftObject?.grossNetIndicator
    ? response.draftObject.grossNetIndicator === 'true'
    : response.draftObject?.totalWithdrawalAmount !== response.draftObject?.amount;

  let amount = response.draftObject?.totalWithdrawalAmount ?? response.draftObject?.amount.toString();
  return {
    ...initialTransactionFormState,
    transactionType: transactionTypes.ach,
    transactionDetails: {
      withdrawalDate: response.draftObject?.requestedWithdrawalDate ?? '',
      completionDate: response.draftObject?.processDate ?? '',
      fundsAvailableToTrade: response.draftObject?.fundsAvailableToTrade ?? '',
      settlementDate: response.draftObject?.processDate ?? '',
      amount: amount,
      amountType: grossNetIndicator ? 'gross' : 'net',
      transferAmount: response.transactionAmount?.toString(),
      contributionReason: '',
      selectedAuthorization: response.draftObject.cashieringRequestId?.toString() ?? '',
      transferDirection: response?.draftObject?.fundsDirection == "I" ? "incoming" : "outgoing",
      trustIncomePercent: response.draftObject.trustIncmPct + '%',
      trustPrincipalPercent: response.draftObject.trustPrinPct + '%',
      trustTaxCategory: response.draftObject.trustTaxCategory,
      trustTaxCode: response.draftObject.trustTaxCode,
      trustComment: response.draftObject.tranMemo,
      trustPaidFor: titleCasePipe.transform(`${response.draftObject.paidFor?.name} - ${response.draftObject.paidFor?.role}`),
      taxWithholdingsFormGroup: {
        fedTaxPercent: response?.draftObject?.fedTaxRate ?? '',
        fedTaxAmount: response.draftObject?.fedTaxAmount?.toString() ?? '',
        stateTaxPercent: response.draftObject?.stateTaxRate,
        stateTaxAmount: response.draftObject?.stateTaxAmount?.toString() ?? '',
        distributionReason: response.draftObject.distributionReasonDescription ?? ''
      },
      secondProcessDate: response.draftObject?.secondProcessDate ?? '',
      recurringFrequency: response.draftObject?.frequency ?? '',
      endDate: response.draftObject?.endDate && datePipe.transform(response.draftObject.endDate,'MM-dd-yyyy')?.toString() !== Constants.defaultMaxDate ? response.draftObject?.endDate : ''
    } as AchTransactionDetails,
    extraPrompts: {
      standingAuthId: response.draftObject?.cashieringRequestId?.toString() ?? '',
      transferDirection: response?.draftObject?.addProfileAttributes ? response?.draftObject?.addProfileAttributes.fundsDirection === "DEP" ? "incoming" : "outgoing" : (response?.draftObject?.fundsDirection == "I" ? "incoming" : "outgoing"),
      frequency: response?.draftObject?.frequency ? (response?.draftObject?.frequency === 'onetime' ? 'onetime' : 'recurring') : 'onetime'
    }
  }
}

function mapCheckTxnDetailsFromInitRelease(response: DraftListAttributes): CheckTransactionDetails {
  let fedTaxPercent: string = response.draftObject.fedTaxAmount.toString();
  let stateTaxPercent: string = response.draftObject.stateTaxAmount.toString();
  let isThirdPartyRecipient: boolean = response.draftObject.recipientAddressType == 'addressdomestic';
  let recurringEndDate: string = '';
  if (response.draftObject.recurringTxn?.endDateYYYY !== 0) {
    recurringEndDate = `${response.draftObject.recurringTxn?.endDateMM}-${response.draftObject.recurringTxn?.endDateDD}-${response.draftObject.recurringTxn?.endDateYYYY}`;
  }
  let recurringSecondDate: string | null = null;
  if(response.draftObject.recurringTxn?.process2ndDateYYYY !== 0) {
    recurringSecondDate = `${response.draftObject.recurringTxn?.process2ndDateMM}-${response.draftObject.recurringTxn?.process2ndDateDD}-${response.draftObject.recurringTxn?.process2ndDateYYYY}`;
  }
  return {
    recipient: checkRecipientTypeDict[response.draftObject.recipientAddressType as keyof typeof checkRecipientTypeDict] as CheckAbbreviatedRecipientTypes,
    additionalPayeeName1: response.draftObject.payeeLine2 ?? '',
    additionalPayeeName2: response.draftObject.payeeLine3 ?? '',
    addresseeName: isThirdPartyRecipient ? response.draftObject.otherDomesticmailTo1 : response.draftObject.otherForeginmailTo1,
    addresseeNameCont: isThirdPartyRecipient ? response.draftObject.otherDomesticmailTo2 : response.draftObject.otherForeginmailTo2,
    addresseeNameCont2: isThirdPartyRecipient ? response.draftObject.otherDomesticmailTo3 : response.draftObject.otherForeginmailTo3,
    amount: response.draftObject.amount ? `$${response.draftObject.amount.toString()}` : '',
    amountType: response.draftObject.amountType ?? '',
    checkMemo: response.draftObject.memo ?? '',
    checkReason: response.draftObject.memoLine2DescriptionCode ?? response.draftObject.MemoLine2DescriptionCode ?? '',
    city: isThirdPartyRecipient ? response.draftObject?.otherDomesticAddrCity ?? '' : response.draftObject?.otherForeginAddrCity ?? '',
    costBasisMethod: '',
    country:  isThirdPartyRecipient ? response.draftObject.recipient?.countryName ?? '' : response.draftObject?.otherForeginAddrCountry ?? '',
    defaultPayee: response.draftObject.defaultPayee ,
    deliveryMethod: response.draftObject.DeliveryMethodCode == 'M' ? 'standard' : 'overnight',
    distributionReasonCode: response.draftObject.distributionReasonCd,
    distributionReasonSubCode: response.draftObject.distributionReasonSubCd,
    endDate: recurringEndDate,
    frequencyDescription: response.draftObject.frequencyDescription ?? '',
    memoLine2: response.draftObject.memoLine2 ?? response.draftObject.MemoLine2 ?? '',
    modifiedPayeeCheckbox: response.draftObject.IsFirstPartyModified,
    modifiedPayeeName: '',
    modifiedPayeeName1: response.draftObject.payeeDetails?.payeeName1 ?? '',
    modifiedPayeeName2: response.draftObject.payeeDetails?.payeeName2 ?? '',
    modifiedPayeeName3: response.draftObject.payeeDetails?.payeeName3 ?? '',
    nextProcessDate: response.draftObject.recurringTxn?.nextProcessDate.toString() ?? '',
    payeeName: response.draftObject.IsFirstPartyModified ? response.draftObject.payeeDetails?.payeeName1 : response.draftObject.payeeLine1 ? response.draftObject.payeeLine1 : response.draftObject.payeeDetails?.payeeName1,
    payeeType: response.draftObject.isThirdParty ? 'thirdParty' : 'firstParty',
    payIncome: response.draftObject?.recurringTxn?.payIncome?.code ?? '',
    postalCode: response.draftObject?.otherForegincAddrZipCode ?? '',
    processDate: `${response.draftObject.processDateMM}-${response.draftObject.processDateDD}-${response.draftObject.processDateYYYY}`,
    recurringFrequency: (response.draftObject.frequency as string),
    secondProcessDate: response.draftObject.process2ndDate ?? recurringSecondDate ?? '',
    state: isThirdPartyRecipient ? response.draftObject?.otherDomesticAddrState : response.draftObject.recipient?.stateName ?? '',
    stateProvince: response.draftObject?.otherForeginAddrState ?? '',
    streetAddress: isThirdPartyRecipient ? response.draftObject?.otherDomesticstreetAddr1 ?? '' : response.draftObject?.otherForeginstreetAddr1 ?? '',
    streetAddressCont: isThirdPartyRecipient ? response.draftObject?.otherDomesticstreetAddr2 ?? '' : response.draftObject?.otherForeginstreetAddr2 ?? '',
    zipCode: isThirdPartyRecipient ? response.draftObject?.otherDomesticAddrZipCode : response.draftObject.recipient?.zipCode ?? '',
    trustTaxCode: response.draftObject.trustTaxCode ?? '',
    trustTaxCategory: response.draftObject.trustTaxCategory ?? '',
    trustComment: response.draftObject.tranMemo ?? '',
    trustIncomePercent: response.draftObject.trustIncmPct ? `${response.draftObject.trustIncmPct}%` : '',
    trustPrincipalPercent: response.draftObject.trustPrinPct ? `${response.draftObject.trustPrinPct}%` : '',
    trustPaidFor: response.draftObject.paidFor?.name ? `${response.draftObject.paidFor?.name}-${response.draftObject.paidFor?.role}` : '',
    taxWithholdingsFormGroup: {
      fedTaxPercent: fedTaxPercent + '%',
      fedTaxAmount: fedTaxPercent,
      stateTaxPercent: stateTaxPercent + '%',
      stateTaxAmount: stateTaxPercent,
      distributionReason: response.draftObject.distributionReasonCdValue ?? ''
    }
  }
}

